import { useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";

import { postCommissionInvoiceForm } from "../../../data";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { FileInput } from "@/components/ui/file-input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/components/ui/use-toast";
import { isExemptionCompanyType } from "@/utils/company-type-options";
import {
  TR_IBAN_PREFIX,
  TR_IBAN_LENGTH,
  formatIban,
  normalizeTrIban,
  validateTrIban,
} from "@/utils/iban";
import {
  BrandHeader,
  ManagerContact,
  SummaryList,
  SummaryRow,
} from "./form-branding";
import { formatTRY } from "@/utils/currency";

// istisna / gider pusulası olan influencer'lar fatura yüklemez,
// sadece ödeme için gerekli bilgileri bu formla iletirler.
export function CommissionInvoiceFormScreen({ path, commission }) {
  const { toast } = useToast();
  const [isDone, setIsDone] = useState(false);

  const needsExemptionDoc = isExemptionCompanyType(
    commission?.client?.companyType
  );

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      recipientName: commission?.influencerName || "",
      email: commission?.email || "",
      iban: TR_IBAN_PREFIX,
      taxNumber: "",
      recipientCity: "",
      isExemptAccount: needsExemptionDoc,
      exemptionDoc: null,
    },
  });

  // Belge alani yalnizca "Bu IBAN bir istisna hesabidir" isaretliyken gorunur.
  // Sirket turu "istisna" olanlarda kutu bastan isaretli gelir.
  const showExemptionDoc = !!watch("isExemptAccount");

  const mutation = useMutation({
    mutationKey: ["postCommissionInvoiceForm", path],
    mutationFn: (formData) =>
      postCommissionInvoiceForm({
        path,
        invoiceId: commission?.invoiceId,
        data: formData,
      }),
    onSuccess: () => {
      setIsDone(true);
      toast({
        title: "Bilgileriniz alındı",
        description: "Ödeme bilgileriniz başarıyla kaydedildi.",
      });
    },
    onError: (error) => {
      const errorData = error?.response?.data;
      if (errorData?.alreadyUploaded) {
        setIsDone(true);
        return;
      }
      toast({
        title: "Hata",
        description:
          errorData?.error || "Bir şeyler ters gitti. Lütfen tekrar deneyin.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (values) => {
    const file = values.exemptionDoc?.[0];

    if (showExemptionDoc && !file) {
      toast({
        title: "İstisna Belgesi gerekli",
        description: "Lütfen istisna belgenizi yükleyin.",
        variant: "destructive",
      });
      return;
    }

    const formData = new FormData();
    // multer dosya adını req.body.fileName'den üretiyor, dosyadan önce eklenmeli.
    formData.append(
      "fileName",
      `istisna-${commission?.influencerName || "belge"}-${commission?.id}`
    );
    formData.append("recipientName", values.recipientName?.trim() || "");
    formData.append("iban", values.iban?.replace(/\s/g, "") || "");
    formData.append("isExemptAccount", values.isExemptAccount ? "true" : "false");
    formData.append("taxNumber", values.taxNumber?.trim() || "");
    formData.append("recipientCity", values.recipientCity?.trim() || "");
    formData.append("email", values.email?.trim() || "");
    if (file) {
      formData.append("file", file);
    }

    mutation.mutate(formData);
  };

  if (isDone) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <BrandHeader />
            <CardTitle>Teşekkürler</CardTitle>
            <CardDescription>
              Ödeme bilgileriniz tarafımıza ulaştı. Bu sayfayı kapatabilirsiniz.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ManagerContact payment={commission?.payment} />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4 py-10">
      <form onSubmit={handleSubmit(onSubmit)} className="w-full max-w-md">
        <Card>
          <CardHeader>
            <BrandHeader />
            <CardTitle>Ödeme Bilgileri Formu</CardTitle>
            <CardDescription>
              Şirket türünüz gereği fatura yüklemenize gerek yok. Ödemenin
              yapılabilmesi için aşağıdaki bilgileri doldurmanız yeterli.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid w-full items-center gap-4">
              <ManagerContact payment={commission?.payment} />

              <SummaryList>
                <SummaryRow
                  label="Proje / Ödeme"
                  value={commission?.payment?.name}
                />
                <SummaryRow
                  label="Ödenecek Tutar"
                  value={formatTRY(commission?.finalAmount)}
                  emphasis
                />
              </SummaryList>

              <div className="flex flex-col space-y-1.5">
                <Label htmlFor="recipientName">
                  Ödeme Yapılacak Kişinin Adı Soyadı *
                </Label>
                <Input
                  id="recipientName"
                  {...register("recipientName", { required: true })}
                />
                {errors.recipientName && (
                  <span className="text-xs text-destructive">Bu alan zorunlu</span>
                )}
              </div>

              <div className="flex flex-col space-y-1.5">
                <Label htmlFor="iban">IBAN *</Label>
                {/* Deger state'te bosluksuz tutulur, ekranda 4'lu gruplar halinde gosterilir. */}
                <input
                  type="hidden"
                  {...register("iban", { validate: validateTrIban })}
                />
                <Input
                  id="iban"
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="TR00 0000 0000 0000 0000 0000 00"
                  // 26 karakter + 6 bosluk
                  maxLength={TR_IBAN_LENGTH + 6}
                  value={formatIban(watch("iban"))}
                  onChange={(e) =>
                    setValue("iban", normalizeTrIban(e.target.value), {
                      shouldValidate: true,
                    })
                  }
                />
                {errors.iban && (
                  <span className="text-xs text-destructive">
                    {errors.iban.message || "Geçersiz IBAN"}
                  </span>
                )}
                <div className="flex items-center gap-2 pt-1">
                  <Checkbox
                    id="isExemptAccount"
                    checked={!!watch("isExemptAccount")}
                    onCheckedChange={(checked) => {
                      setValue("isExemptAccount", !!checked);
                      // Kutu kaldirilinca secili belge de birakilsin
                      if (!checked) setValue("exemptionDoc", null);
                    }}
                  />
                  <Label htmlFor="isExemptAccount" className="font-normal">
                    Bu IBAN bir istisna hesabıdır
                  </Label>
                </div>

                {showExemptionDoc && (
                  <div className="mt-3 flex flex-col space-y-1.5 rounded-md border bg-muted/40 p-3">
                    <Label htmlFor="exemptionDoc">İstisna Belgesi *</Label>
                    <FileInput
                      id="exemptionDoc"
                      accept=".pdf,.jpg,.jpeg,.png"
                      {...register("exemptionDoc", {
                        required: showExemptionDoc,
                      })}
                    />
                    <span className="text-xs text-muted-foreground">
                      PDF veya görsel (JPG/PNG) yükleyebilirsiniz.
                    </span>
                    {errors.exemptionDoc && (
                      <span className="text-xs text-destructive">
                        Bu alan zorunlu
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div className="flex flex-col space-y-1.5">
                <Label htmlFor="taxNumber">T.C. Kimlik Numarası *</Label>
                <Input
                  id="taxNumber"
                  {...register("taxNumber", { required: true })}
                />
                {errors.taxNumber && (
                  <span className="text-xs text-destructive">Bu alan zorunlu</span>
                )}
              </div>

              <div className="flex flex-col space-y-1.5">
                <Label htmlFor="recipientCity">Yaşadığı İl *</Label>
                <Input
                  id="recipientCity"
                  {...register("recipientCity", { required: true })}
                />
                {errors.recipientCity && (
                  <span className="text-xs text-destructive">Bu alan zorunlu</span>
                )}
              </div>

              <div className="flex flex-col space-y-1.5">
                <Label htmlFor="email">E-posta Adresi *</Label>
                <Input
                  id="email"
                  type="email"
                  {...register("email", { required: true })}
                />
                {errors.email && (
                  <span className="text-xs text-destructive">Bu alan zorunlu</span>
                )}
              </div>
            </div>
          </CardContent>
          <CardFooter>
            <Button
              type="submit"
              className="w-full"
              disabled={mutation.isPending}
            >
              {mutation.isPending ? "Gönderiliyor..." : "Bilgileri Gönder"}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </div>
  );
}
